import { useMemo } from 'react';
import { useMe } from '@/auth/useAuth';

/**
 * Per-tenant feature toggles (set by the Nguyên Bình platform admin). `/identity/me` answers `features` = the codes enabled
 * for the signed-in user's company; permissions in `me` are already masked by disabled features. Hiding is cosmetic — an
 * endpoint of a disabled feature answers 404 FEATURE_DISABLED.
 *
 * Old API (no `features` in `me`, during rollout) → every feature counts as enabled.
 */
export const FEATURE = {
  leads: 'crm.leads',
  opportunities: 'crm.opportunities',
  classification: 'crm.classification',
  legalProfileRequired: 'crm.legal_profile_required',
  quotes: 'sales.quotes',
  quoteTemplates: 'sales.quote_templates',
  approvalPolicies: 'approvals.policies',
  delegations: 'approvals.delegations',
  orderFirst: 'commercial.order_first',
  signature: 'commercial.signature',
  contractTypes: 'commercial.contract_types',
  changeRequests: 'commercial.change_requests',
  resourceCalendar: 'delivery.resource_calendar',
  financeDesk: 'finance.desk',
  receipts: 'finance.receipts',
  receivables: 'finance.receivables',
  invoices: 'finance.invoices',
  reconciliation: 'finance.reconciliation',
  refunds: 'finance.refunds',
  financeAdjustments: 'finance.adjustments',
  commissionPolicyEditor: 'commission.policy_editor',
  commissionAdjustments: 'commission.adjustments',
  commissionPayouts: 'commission.payouts',
  teams: 'admin.teams',
  numberSeries: 'admin.number_series',
  settingsRaw: 'admin.settings_raw',
  dashboardTeam: 'dashboard.team',
} as const;

export type FeatureCode = (typeof FEATURE)[keyof typeof FEATURE];

/** Every toggleable feature code the web knows. Anything else is core (always on). */
export const FEATURE_CODES: readonly FeatureCode[] = Object.values(FEATURE);

/** Enabled codes, or `null` = the API did not send the list (treat everything as enabled). */
export type FeatureSet = ReadonlySet<string> | null;

export function toFeatureSet(features: readonly string[] | null | undefined): FeatureSet {
  return Array.isArray(features) ? new Set(features) : null;
}

/** Off only when the API sent a list without this code. No code = core = on. */
export function isFeatureOn(features: FeatureSet, code: string | undefined | null): boolean {
  if (!code || !features) return true;
  return features.has(code);
}

export interface FeatureAccess {
  /** Raw set (null = old API, all on). */
  features: FeatureSet;
  profile: string | null;
  has: (code: string | undefined | null) => boolean;
}

export function featureAccess(features: FeatureSet, profile: string | null = null): FeatureAccess {
  return { features, profile, has: (code) => isFeatureOn(features, code) };
}

/** Feature access for the signed-in user's company (from the `me` query). */
export function useFeatures(): FeatureAccess {
  const { data } = useMe();
  const list = data?.features;
  const profile = data?.featureProfile ?? null;
  return useMemo(() => featureAccess(toFeatureSet(list), profile), [list, profile]);
}

/** `useFeature('sales.quotes')` → false when the platform admin switched it off for this company. */
export function useFeature(code: string): boolean {
  return useFeatures().has(code);
}

/**
 * Permission-code prefix → feature, used only when the API does not send `featureEnabled` / `featureCode` on permissions.
 * Longest prefix wins.
 */
const PERMISSION_FEATURE_PREFIXES: [string, FeatureCode][] = [
  ['crm.lead.', FEATURE.leads],
  ['crm.opportunity.', FEATURE.opportunities],
  ['crm.customer.classification.', FEATURE.classification],
  ['sales.quote.template.', FEATURE.quoteTemplates],
  ['sales.quote.', FEATURE.quotes],
  ['approvals.policy.', FEATURE.approvalPolicies],
  ['identity.delegations.', FEATURE.delegations],
  ['commercial.contract_type.', FEATURE.contractTypes],
  ['commercial.change_request.', FEATURE.changeRequests],
  ['finance.receipt.', FEATURE.receipts],
  ['finance.allocation.', FEATURE.receipts],
  ['finance.receivable.', FEATURE.receivables],
  ['finance.schedule.', FEATURE.receivables],
  ['finance.invoice.', FEATURE.invoices],
  ['finance.reconciliation.', FEATURE.reconciliation],
  ['finance.refund.', FEATURE.refunds],
  ['finance.adjustment.', FEATURE.financeAdjustments],
  ['commission.policy.manage', FEATURE.commissionPolicyEditor],
  ['commission.adjustment.', FEATURE.commissionAdjustments],
  ['commission.payout.', FEATURE.commissionPayouts],
  ['commission.schedule.', FEATURE.commissionPayouts],
];

export function permissionFeature(code: string): FeatureCode | null {
  let best: [string, FeatureCode] | null = null;
  for (const entry of PERMISSION_FEATURE_PREFIXES) if (code.startsWith(entry[0]) && (!best || entry[0].length > best[0].length)) best = entry;
  return best?.[1] ?? null;
}

/** Whether a permission's feature is on: the API flag when present, else its feature code, else the prefix map. */
export function permissionFeatureOn(
  p: { code: string; featureCode?: string | null; featureEnabled?: boolean },
  features: FeatureSet,
): boolean {
  if (typeof p.featureEnabled === 'boolean') return p.featureEnabled;
  const code = p.featureCode !== undefined ? p.featureCode : permissionFeature(p.code);
  return isFeatureOn(features, code);
}
